import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Periodicidade, periodicidadeMensal } from '../../../../shared';
import { DatepickerComponent, datepickerModePeriodicidade } from '../../../../shared/components/datepicker';
import { AuthService, BaseFilterCollapse, BaseFilterCollapseService } from '../../../../core';
import { CampanhaFilter } from './campanha-filter';
import { CampanhaFilterStorageService } from './campanha-filter-storage.service';
import { CampanhaFilterInitialization } from './campanha-filter-initialization';
import { Pessoa } from '../../../pessoa/pessoa';
import { Cliente } from '../../../pessoa/cliente/cliente';
import { EnumUsuarioPerfil } from 'app/domains/shared/components/usuario-multi-select/enums/usuario-perfil.enum';
import { EnumSituacao } from 'app/domains/shared/enums/situacao.enum';

interface Config {
    isShowRepresentantes: boolean;
    isShowCliente: boolean;
}

@Component({
    selector: 'sgt-campanha-filter',
    templateUrl: './campanha-filter.component.html',
    styleUrls: ['./campanha-filter.component.less']
})
export class CampanhaFilterComponent implements BaseFilterCollapse, OnInit, AfterViewInit, OnChanges, OnDestroy {
    readonly EnumUsuarioPerfil = EnumUsuarioPerfil;
    readonly EnumSituacao = EnumSituacao;

    config: Config;

    /** Se é um representante */
    isRepresentante: boolean;

    filterPsqSubscription: Subscription;
    @Input() storageKey: string;
    @Input() filter: CampanhaFilter;
    @Input() initialization: CampanhaFilterInitialization;
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterCleaned = new EventEmitter<any>();

    // Options
    /** Se deve exibir o filtro dos representantes */
    @Input() isShowRepresentantes = true;
    /** Se deve exibir o filtro do cliente */
    @Input() isShowCliente = true;


    // Filter collapse
    @Input() isCollapsedInDefault = false;
    isCollapsedIn = false;
    @ViewChild('filterCollapse') filterCollapse: ElementRef;
    collapseIcon = 'fas fa-caret-down';

    isFirstChangeFilterPsq = true;
    @ViewChild('filterPsq', {static: true}) filterPsq: NgModel;

    // Data Vigência Datepicker
    dataVigenciaDatepickerMode = datepickerModePeriodicidade.id;
    isFirstChangeDataVigencia = true;
    @ViewChild('dataVigenciaDatepicker') dataVigenciaDatepicker: DatepickerComponent;

    // Multiselects
    isFirstChangeRepresentanteMultiselect = true;
    isFirstChangePacotesMultiselect = true;

    constructor(private authService: AuthService,
                private baseFilterCollapseService: BaseFilterCollapseService,
                private campanhaFilterStorageService: CampanhaFilterStorageService) { }


    ngOnInit() {
        this.isRepresentante = this.authService.isRepresentante();

        // Aplicando o filtro salvo no storage
        this.campanhaFilterStorageService.applyFilter(this.storageKey, this.filter);
        // Iniciando filtro
        this.initFilter();

        this.setConfig();
    }

    private setConfig() {
        this.config = {
            isShowRepresentantes: this.isShowRepresentantes && !this.isRepresentante,
            isShowCliente: this.isShowCliente
        }
    }

    ngAfterViewInit() {
        if (!this.isCollapsedInDefault) {
            jQuery(this.filterCollapse.nativeElement).css('display', 'none');
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'isCollapsedInDefault') {
                if (changes[propName].isFirstChange()) {
                    setTimeout(() => {
                        this.isCollapsedIn = changes[propName].currentValue;
                    }, 100);
                }
            }
        }
    }

    /** Inicializa o filtro */
    private initFilter() {
        // Filter de pesquisa
        this.subscribeToFilterPsq();

        setTimeout(() => {
            // Setando o mês atual quando o filtro não possuir a periodicidade de vigência
            if (!this.filter.possuiPeriodicidadeVigencia()) {
                this.setMesVigenciaAtual();
            }

            this.initialization.initializeMain();
        }, 200);
    }

    // Realiza o subscribe nas alterações feitas no input do filtro da pesquisa
    private subscribeToFilterPsq() {
        this.filterPsqSubscription = this.filterPsq.valueChanges
            .pipe(
                debounceTime(1000), // Dando um debounce de 1000ms para emitir a alteração
                map(emittedValue => emittedValue?.trim()),
                distinctUntilChanged()
            )
            .subscribe(() => {
                if (this.isFirstChangeFilterPsq) {
                    this.isFirstChangeFilterPsq = false;
                    return;
                }

                if (this.filter.filterPsq?.trim().length > 0) {
                    this.filter.isFilterDirty = true;
                }

                this.filterChange.emit();
            });
    }

    /** Emite evento de alteração no filtro para atualizar a pesquisa */
    refresh() {
        this.close();
        this.filterChange.emit();
    }

    /** Limpa o filtro e atualiza a pesquisa */
    clear() {
        // Removendo os filtros
        this.baseFilterCollapseService.clear(this);
        this.close();
        this.filterCleaned.emit();
    }

    /** Abre/Fecha o filtro */
    toggleCollapse() {
        this.baseFilterCollapseService.toggleCollapse(this);
    }

    /** Abre o filtro */
    open() {
        this.baseFilterCollapseService.open(this);
    }

    /** Fecha o filtro */
    close() {
        this.baseFilterCollapseService.close(this);
    }

    /** Seta o foco no datepicker da data de vigência */
    setFocusDataVigenciaDatepicker() {
        this.dataVigenciaDatepicker?.setFocus();
    }

    /** Seta a data do filtro com periodicidade mensal e com o mês atual */
    setMesVigenciaAtual() {
        const data = new Date();
        data.setDate(1); // Setando sempre o dia 1;

        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeMensal, data);
    }

    /** Seta a periodicidade da vigência no filtro da data de vigência */
    setVigenciaAtual(periodicidadeVigencia: Periodicidade) {
        const data = new Date();
        data.setDate(1); // Setando sempre o dia 1;

        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeVigencia, data);
    }

    /** Ao alterar a data de vigência */
    onChangeDataVigencia() {
        if (this.isFirstChangeDataVigencia) {
            this.isFirstChangeDataVigencia = false;
            return;
        }

        this.filterChange.emit();
    }

    /** Ao alterar a data de início do período de vigência */
    onChangeDataInicioVigenciaPeriodo() {
        this.filterChange.emit();
    }

    /** Ao alterar a data de término do período de vigência */
    onChangeDataTerminoVigenciaPeriodo() {
        this.filterChange.emit();
    }

    /** Ao alterar a flag de somente em uso */
    onChangeSomenteEmUso(somenteEmUso: boolean) {
        this.filter.somenteEmUso = somenteEmUso;
        this.filterChange.emit();
    }

    /** Ao alterar os status */
    onChangeStatuses() {
        this.filterChange.emit();
    }

    /** Ao alterar os representantes */
    onChangeRepresentantes() {
        if (this.isFirstChangeRepresentanteMultiselect) {
            this.isFirstChangeRepresentanteMultiselect = false;
            return;
        }

        this.filterChange.emit();
    }

    /** Ao alterar os pacotes */
    onChangePacotes() {
        if (this.isFirstChangePacotesMultiselect) {
            this.isFirstChangePacotesMultiselect = false;
            return;
        }

        this.filterChange.emit();
    }

    /** Ao selecionar o cliente vinculado à campanha */
    onSelectClienteVinculado(cliente: Cliente) {
        if (cliente) {
            this.filter.idClienteVinculado = cliente.id;
            this.filter.somenteClientesVinculados = true;
        } else {
            this.clearClienteVinculado();
        }
    }

    /** Remove o cliente vinculado à campanha */
    clearClienteVinculado() {
        this.filter.idClienteVinculado = null;
        this.filter.clienteVinculado = null;
        this.filter.somenteClientesVinculados = false;
    }

    /** Ao selecionar a pessoa do cliente da promoção */
    onChangeClientePromocao(pessoa: Pessoa) {
        if (pessoa) {
            this.filter.idPessoa = pessoa.id;
        } else {
            this.clearClientePromocao();
        }
    }

    /** Remove a pessoa do cliente da promoção */
    clearClientePromocao() {
        this.filter.idPessoa = null;
        this.filter.pessoa = null;
    }

    /** Ao alterar os tipos de promoções */
    onChangeTiposPromocoes() {
        this.filter.isFilterDirty = true;
    }

    /** Ao alterar os gerentes */
    onChangeGerentes() {
        this.filter.isFilterDirty = true;
    }

    /** Ao alterar os atendentes */
    onChangeAtendentes() {
        this.filter.isFilterDirty = true;
    }

    ngOnDestroy() {
        this.filterPsqSubscription?.unsubscribe();
    }
}
